import { useNavigate } from 'react-router';
import { Clock } from 'lucide-react';
import {
  DASHBOARD_STATUS_LABELS,
  DASHBOARD_STATUS_STYLE,
  type DashboardOrder,
} from '../lib/dashboardData';
import { SectionHeader } from './SectionHeader';

export interface TodaysActivityPanelProps {
  /** Orders with at least one task scheduled today, sorted by earliest time. */
  orders: DashboardOrder[];
  /** ISO date "YYYY-MM-DD" for "today" — used to pick the right task per order. */
  todayIso: string;
  /** Max rows to render. Defaults to 7. */
  limit?: number;
}

/**
 * "Today's Activity" panel — clickable list of today's jobs with status
 * badge and earliest scheduled time.
 */
export function TodaysActivityPanel({ orders, todayIso, limit = 7 }: TodaysActivityPanelProps) {
  const navigate = useNavigate();
  const rows = orders.slice(0, limit);

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
      <SectionHeader
        icon={Clock}
        iconColor="text-orange-500"
        title="Today's Activity"
        onLink={() => navigate('/orders')}
      />
      {rows.length === 0 ? (
        <div className="px-3 py-8 text-center text-xs italic text-slate-400">
          No jobs scheduled for today.
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rows.map((order) => {
            const task = order.tasks.find((t) => t.scheduledDate === todayIso);
            return (
              <button
                key={order.id}
                type="button"
                onClick={() => navigate(`/orders/${order.id}`)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-slate-700">{order.jobNumber}</span>
                    <span
                      className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-medium ${DASHBOARD_STATUS_STYLE[order.status]}`}
                    >
                      {DASHBOARD_STATUS_LABELS[order.status]}
                    </span>
                  </div>
                  <div className="truncate text-[11px] text-slate-500">
                    {order.customer}
                    {task?.locationName || task?.city ? ` · ${task.locationName || task.city}` : ''}
                  </div>
                </div>
                <div className="flex-shrink-0 text-[11px] font-medium tabular-nums text-slate-400">
                  {task?.timeFrom ?? '—'}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
